import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Trash2, Minus, Plus, ArrowRight, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useCart, useAuth } from "@/App";
import { toast } from "sonner";
import { SEO } from "@/components/SEO";

const FREQUENCIES = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
];

export default function CartPage() {
  const { cart, updateCart, removeFromCart } = useCart();
  const { user } = useAuth();

  const handleQuantityChange = async (productId, currentQuantity, delta) => {
    const newQuantity = currentQuantity + delta;
    if (newQuantity < 1) {
      await removeFromCart(productId);
      toast.success("Item removed from cart");
      return;
    }
    
    const updatedItems = cart.items.map(item => ({
      product_id: item.product_id,
      quantity: item.product_id === productId ? newQuantity : item.quantity,
      subscription: item.subscription,
      subscription_frequency: item.subscription_frequency
    }));
    
    await updateCart(updatedItems);
  };

  const handleRemove = async (productId) => {
    await removeFromCart(productId);
    toast.success("Item removed from cart");
  };

  const toggleSubscription = async (productId, currentSub) => {
    const updatedItems = cart.items.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      subscription: item.product_id === productId ? !currentSub : item.subscription,
      subscription_frequency: item.product_id === productId ? (!currentSub ? "weekly" : null) : item.subscription_frequency
    }));
    await updateCart(updatedItems);
    toast.success(!currentSub ? "Subscription enabled - Save 10%!" : "Subscription removed");
  };

  const changeFrequency = async (productId, frequency) => {
    const updatedItems = cart.items.map(item => ({
      product_id: item.product_id,
      quantity: item.quantity,
      subscription: item.subscription,
      subscription_frequency: item.product_id === productId ? frequency : item.subscription_frequency
    }));
    await updateCart(updatedItems);
  };

  const subscriptionDiscount = cart.items?.reduce((acc, item) => {
    if (item.subscription) return acc + (item.item_total || 0) * 0.1;
    return acc;
  }, 0) || 0;

  if (!user) {
    return (
      <div className="pt-16 md:pt-20 lg:pt-24 min-h-[60vh] flex items-center justify-center bg-background" data-testid="cart-login-prompt">
        <SEO title="Shopping Cart" path="/cart" />
        <div className="text-center px-4">
          <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-semibold text-praroha-forest mb-2">
            Login to View Your Cart
          </h1>
          <p className="text-muted-foreground mb-6">
            Please login to add items to your cart and complete your purchase.
          </p>
          <Link to="/login">
            <Button className="bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full px-8 py-6">
              Login
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="pt-16 md:pt-20 lg:pt-24 min-h-[60vh] flex items-center justify-center bg-background" data-testid="cart-empty">
        <SEO title="Shopping Cart" path="/cart" />
        <div className="text-center px-4">
          <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-semibold text-praroha-forest mb-2">
            Your Cart is Empty
          </h1>
          <p className="text-muted-foreground mb-6">
            Looks like you haven't added any microgreens yet.
          </p>
          <Link to="/products">
            <Button className="bg-praroha-forest hover:bg-praroha-forest/90 text-white rounded-full px-8 py-6">
              <ArrowLeft className="mr-2 w-5 h-5" />
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 lg:pt-24" data-testid="cart-page">
      <SEO title="Shopping Cart" path="/cart" />
      {/* Header */}
      <section className="py-8 bg-praroha-cream/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-praroha-forest">
            Shopping Cart
          </h1>
          <p className="text-muted-foreground mt-1">
            {cart.items.length} item{cart.items.length !== 1 ? 's' : ''} in your cart
          </p>
        </div>
      </section>

      {/* Cart Content */}
      <section className="py-12 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item, index) => (
                <motion.div
                  key={item.product_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="overflow-hidden border-border/50" data-testid={`cart-item-${index}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        {/* Product Image */}
                        <Link to={`/products/${item.product?.slug}`} className="flex-shrink-0">
                          <img
                            src={item.product?.image}
                            alt={item.product?.name}
                            className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-xl"
                          />
                        </Link>

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start">
                            <div>
                              <Link to={`/products/${item.product?.slug}`}>
                                <h3 className="font-heading text-lg font-semibold text-praroha-forest hover:text-praroha-sage transition-colors">
                                  {item.product?.name}
                                </h3>
                              </Link>
                              <p className="text-sm text-muted-foreground mt-1">
                                ₹{item.product?.price} each
                              </p>
                              {item.subscription && (
                                <span className="inline-block mt-2 px-2 py-1 bg-praroha-sage/10 text-praroha-sage text-xs rounded-full">
                                  {item.subscription_frequency} subscription
                                </span>
                              )}
                            </div>
                            <button
                              onClick={() => handleRemove(item.product_id)}
                              className="p-2 text-muted-foreground hover:text-destructive transition-colors"
                              data-testid={`remove-item-${index}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>

                          {/* Quantity & Total */}
                          <div className="flex items-center justify-between mt-4">
                            <div className="flex items-center border border-border rounded-full">
                              <button
                                onClick={() => handleQuantityChange(item.product_id, item.quantity, -1)}
                                className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-praroha-forest transition-colors"
                                data-testid={`decrease-quantity-${index}`}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-10 text-center font-medium">{item.quantity}</span>
                              <button
                                onClick={() => handleQuantityChange(item.product_id, item.quantity, 1)}
                                className="w-8 h-8 flex items-center justify-center text-muted-foreground hover:text-praroha-forest transition-colors"
                                data-testid={`increase-quantity-${index}`}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="font-heading text-lg font-semibold text-praroha-forest">
                              ₹{item.item_total?.toFixed(0)}
                            </p>
                            </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="sticky top-24 border-border/50 shadow-soft" data-testid="order-summary">
                <CardContent className="p-6">
                  <h2 className="font-heading text-xl font-semibold text-praroha-forest mb-6">
                    Order Summary
                  </h2>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span>₹{cart.total?.toFixed(0)}</span>
                    </div>
                    {subscriptionDiscount > 0 && (
                      <div className="flex justify-between text-praroha-sage">
                        <span>Subscription Discount (10%)</span>
                        <span>-₹{subscriptionDiscount.toFixed(0)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-muted-foreground">
                      <span>Shipping</span>
                      <span className="text-praroha-sage">Free</span>
                    </div>
                    <div className="border-t border-border pt-3 flex justify-between">
                      <span className="font-heading text-lg font-semibold text-praroha-forest">Total</span>
                      <span className="font-heading text-lg font-semibold text-praroha-forest" data-testid="cart-total">
                        ₹{(cart.total - subscriptionDiscount)?.toFixed(0)}
                      </span>
                    </div>
                  </div>

                  <Link to="/checkout" data-testid="checkout-btn">
                    <Button className="w-full bg-praroha-terracotta hover:bg-praroha-terracotta/90 text-white rounded-full py-6 text-lg">
                      Proceed to Checkout
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </Link>

                  <Link to="/products" className="block mt-4">
                    <Button variant="outline" className="w-full rounded-full border-praroha-forest/30 text-praroha-forest">
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Continue Shopping
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
